import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { db } from '../../firebase';
import { collectionGroup, onSnapshot, collection } from 'firebase/firestore';

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const REACTIONS = [
  { key: 'accepted', emoji: '✅', label: 'Принято' },
  { key: 'done', emoji: '🎯', label: 'Выполнено' },
  { key: 'postponed', emoji: '⏳', label: 'Отложено' },
];

const pad = (n) => String(n).padStart(2, '0');
const dateKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const keyOf = (date) => dateKey(date.getFullYear(), date.getMonth(), date.getDate());
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const weekStart = (d) => {
  const r = startOfDay(d);
  const wd = (r.getDay() + 6) % 7;
  r.setDate(r.getDate() - wd);
  return r;
};
const eventStart = (p) => {
  const [y, m, d] = (p.eventDate || '1970-01-01').split('-').map(Number);
  const [hh, mm] = (p.eventTime || '00:00').split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0);
};
const snippet = (text, len) => {
  const t = text || '';
  return t.length > len ? t.slice(0, len) + '…' : t;
};

const navBtn = {
  background: '#f0f2f5', border: 'none', borderRadius: '8px',
  padding: '4px 10px', cursor: 'pointer', fontWeight: 700, color: '#333',
};
const tabStyle = (active) => ({
  padding: '6px 14px', borderRadius: '16px', border: 'none', cursor: 'pointer',
  fontWeight: 600, fontSize: '13px',
  background: active ? '#2a5298' : '#f0f2f5',
  color: active ? '#fff' : '#333',
});

const EventCalendar = () => {
  const user = useSelector((state) => state.auth.user);
  const [allPosts, setAllPosts] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [usersList, setUsersList] = useState([]);
  const [mode, setMode] = useState('month');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [now, setNow] = useState(new Date());
  const [toasts, setToasts] = useState([]);
  const [calUserId, setCalUserId] = useState(null); // чей календарь смотрим
  const notifiedRef = useRef({});

  const isStaff = user?.role === 'admin' || user?.role === 'moderator';
  const effectiveUid = calUserId || user?.uid;
  const effectiveName = calUserId ? (usersById[calUserId] || 'пользователь') : null;

  // Все посты ленты и групп
  useEffect(() => {
    const unsubscribe = onSnapshot(collectionGroup(db, 'posts'), (snap) => {
      setAllPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => console.error('Ошибка загрузки постов для календаря:', error));
    return () => unsubscribe();
  }, []);

  // Все личные сообщения (диалоги + монологи)
  useEffect(() => {
    const unsubscribe = onSnapshot(collectionGroup(db, 'messages'), (snap) => {
      setAllMessages(snap.docs.map((d) => ({ id: 'm_' + d.id, ...d.data(), source: 'chat' })));
    }, (error) => console.error('Ошибка загрузки сообщений для календаря:', error));
    return () => unsubscribe();
  }, []);

  // Пользователи: карта имён + список
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsersList(list);
      const map = {};
      list.forEach((u) => {
        map[u.id] = u.displayName || (u.email ? u.email.split('@')[0] : 'Пользователь');
      });
      setUsersById(map);
    });
    return () => unsubscribe();
  }, []);

  // Тикер времени
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // События ВЫБРАННОГО пользователя (посты + сообщения)
  const myEvents = useMemo(() => {
    if (!effectiveUid) return [];
    const fromPosts = allPosts.filter((p) =>
      p.eventDate &&
      ((p.taggedUsers || []).includes(effectiveUid) || p.authorId === effectiveUid)
    );
    const fromMessages = allMessages.filter((m) =>
      m.eventDate && (m.from === effectiveUid || m.to === effectiveUid)
    );
    return [...fromPosts, ...fromMessages];
  }, [allPosts, allMessages, effectiveUid]);

  const eventsByDate = useMemo(() => {
    const map = {};
    myEvents.forEach((p) => {
      (map[p.eventDate] = map[p.eventDate] || []).push(p);
    });
    return map;
  }, [myEvents]);

  const todayKey = keyOf(new Date());

  // Напоминания за 10 минут — только для СВОЕГО календаря
  useEffect(() => {
    if (calUserId) return;
    myEvents.forEach((p) => {
      if (!p.eventTime) return;
      const ml = (eventStart(p).getTime() - now.getTime()) / 60000;
      if (ml > 0 && ml <= 10 && !notifiedRef.current[p.id]) {
        notifiedRef.current[p.id] = true;
        const msg = `🔥 Через ${Math.max(1, Math.round(ml))} мин: ${snippet(p.text, 60) || 'событие'}`;
        setToasts((t) => [...t, { id: p.id, msg }]);
        setTimeout(() => setToasts((t) => t.filter((x) => x.id !== p.id)), 15000);
      }
    });
  }, [now, myEvents, calUserId]);

  const minutesLeft = (p) => (eventStart(p).getTime() - now.getTime()) / 60000;

  const myReaction = (p) =>
    REACTIONS.map((r) => r.key).find((k) => ((p.reactions || {})[k] || []).includes(effectiveUid));

  const urgencyLevel = (p) => {
    if (!p.eventTime) return 0;
    const ml = minutesLeft(p);
    if (ml <= 0 || ml > 60) return 0;
    if (ml <= 10) return 3;
    if (ml <= 30) return 2;
    return 1;
  };

  const urgencyStyle = (u) =>
    u === 3 ? { background: '#8b0000', color: '#fff' }
    : u === 2 ? { background: '#e53935', color: '#fff' }
    : u === 1 ? { background: '#fdd835', color: '#333' }
    : { background: '#e8f5e9', color: '#333' };

  const cellColor = (events) => {
    const u = Math.max(0, ...events.map(urgencyLevel));
    if (u === 3) return { background: '#8b0000', color: '#fff' };
    if (u === 2) return { background: '#e53935', color: '#fff' };
    if (u === 1) return { background: '#fdd835', color: '#333' };
    const my = events.map(myReaction).find(Boolean);
    if (my === 'done') return { background: '#2a5298', color: '#fff' };
    if (my === 'postponed') return { background: '#ff9800', color: '#fff' };
    if (my === 'accepted') return { background: '#4caf50', color: '#fff' };
    return { background: '#66bb6a', color: '#fff' };
  };

  const reactionSummary = (p) =>
    REACTIONS.map((r) => `${r.emoji}${((p.reactions || {})[r.key] || []).length}`).join(' ');

  // Реакции текстом (без иконок) — для XLS
  const reactionText = (p) =>
    REACTIONS.map((r) => {
      const n = ((p.reactions || {})[r.key] || []).length;
      return n > 0 ? `${r.label}: ${n}` : null;
    }).filter(Boolean).join(', ') || '—';

  const sourceOf = (p) => (p.groupId ? 'Группа' : p.source === 'chat' ? 'Диалог' : 'Лента');

  const ReactionBadges = ({ p }) => (
    <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
      {REACTIONS.map((r) => {
        const uids = (p.reactions || {})[r.key] || [];
        if (uids.length === 0) return null;
        const mine = uids.includes(effectiveUid);
        return (
          <span key={r.key}
            title={`${r.label}: ${uids.map((id) => usersById[id] || '—').join(', ')}`}
            style={{
              fontSize: '11px',
              background: mine ? '#e3f2fd' : '#f5f5f5',
              border: mine ? '1px solid #2a5298' : '1px solid transparent',
              borderRadius: '10px', padding: '1px 6px',
              color: mine ? '#1e3c72' : '#666',
              fontWeight: mine ? 700 : 400,
            }}>
            {r.emoji} {uids.length}
          </span>
        );
      })}
    </span>
  );

  const shift = (dir) => {
    setAnchor((a) => {
      const d = new Date(a);
      if (mode === 'month') d.setMonth(d.getMonth() + dir);
      else if (mode === 'week') d.setDate(d.getDate() + dir * 7);
      else d.setDate(d.getDate() + dir);
      return startOfDay(d);
    });
  };

  const label =
    mode === 'month'
      ? `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
      : mode === 'week'
      ? (() => {
          const s = weekStart(anchor);
          const e = new Date(s);
          e.setDate(e.getDate() + 6);
          return `${pad(s.getDate())}.${pad(s.getMonth() + 1)} – ${pad(e.getDate())}.${pad(e.getMonth() + 1)}.${e.getFullYear()}`;
        })()
      : `${pad(anchor.getDate())}.${pad(anchor.getMonth() + 1)}.${anchor.getFullYear()}`;

  // Диапазон отображаемого периода (месяц / неделя / день)
  const getExportRange = () => {
    if (mode === 'month') {
      const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      return [start, end];
    } else if (mode === 'week') {
      const start = weekStart(anchor);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return [start, end];
    } else {
      const start = startOfDay(anchor);
      return [start, start];
    }
  };

  // Выгрузка XLS — ТОЛЬКО события отображаемого периода
  const exportXls = () => {
    const [rangeStart, rangeEnd] = getExportRange();
    const startKey = keyOf(rangeStart);
    const endKey = keyOf(rangeEnd);
    const rows = myEvents
      .filter((p) => p.eventDate >= startKey && p.eventDate <= endKey)
      .slice()
      .sort((a, b) => (a.eventDate < b.eventDate ? -1 : 1))
      .map((p) => ({
        source: sourceOf(p),
        date: p.eventDate ? p.eventDate.split('-').reverse().join('.') : '',
        time: p.eventTime || '',
        author: (p.authorName || p.authorEmail || '').replace(/</g, '&lt;'),
        reactions: reactionText(p),
        text: (p.text || '(файл)').replace(/</g, '&lt;'),
      }));

    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"></head><body>
      <table border="1">
        <tr><th>Источник</th><th>Дата</th><th>Время</th><th>Автор</th><th>Реакции</th><th>Текст</th></tr>
        ${rows.map((r) => `<tr><td>${r.source}</td><td>${r.date}</td><td>${r.time}</td><td>${r.author}</td><td>${r.reactions}</td><td>${r.text}</td></tr>`).join('')}
      </table></body></html>`;

    const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const periodLabel = `${startKey}_${endKey}`;
    const fname = calUserId
      ? `kalendar_${(effectiveName || 'user').replace(/[^\wа-яА-ЯёЁ-]+/g, '_')}_${periodLabel}.xls`
      : `posty_s_datami_${periodLabel}.xls`;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------- РЕЖИМЫ ---------- */

  const renderMonth = () => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const startWeekday = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
        {WEEKDAYS.map((w) => (
          <div key={w} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#888', padding: '4px 0' }}>{w}</div>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <div key={'empty' + i} />;
          const key = dateKey(anchor.getFullYear(), anchor.getMonth(), d);
          const events = eventsByDate[key] || [];
          const has = events.length > 0;
          const isToday = key === todayKey;
          const tooltip = has
            ? events.map((p) =>
                `${p.eventTime ? p.eventTime + ' — ' : ''}${p.text || '(файл)'} [${sourceOf(p)}] | ${reactionSummary(p)}`
              ).join('\n')
            : '';
          return (
            <div key={key} title={tooltip}
              style={{
                minHeight: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '8px', fontSize: '13px', cursor: has ? 'pointer' : 'default',
                ...(has ? cellColor(events) : { background: 'rgba(255,255,255,0.5)', color: '#333' }),
                fontWeight: has ? 700 : 400,
                outline: isToday ? '2px solid #2a5298' : 'none',
              }}>
              {d}
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeek = () => {
    const s = weekStart(anchor);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(s);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
        {days.map((day) => {
          const key = keyOf(day);
          const events = (eventsByDate[key] || []).slice().sort((a, b) => (a.eventTime || '').localeCompare(b.eventTime || ''));
          const isToday = key === todayKey;
          return (
            <div key={key} style={{ background: 'rgba(255,255,255,0.5)', borderRadius: '8px', padding: '6px', minHeight: '140px', outline: isToday ? '2px solid #2a5298' : 'none' }}>
              <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: isToday ? '#2a5298' : '#888', marginBottom: '6px' }}>
                {WEEKDAYS[(day.getDay() + 6) % 7]} {pad(day.getDate())}
              </div>
              {events.map((p) => (
                <div key={p.id} title={p.text || ''}
                  style={{ ...urgencyStyle(urgencyLevel(p)), marginBottom: '6px', padding: '6px', borderRadius: '6px', fontSize: '11px' }}>
                  <div style={{ fontWeight: 700 }}>{p.eventTime || '—'}</div>
                  <div style={{ margin: '2px 0' }}>{snippet(p.text, 40)}</div>
                  <ReactionBadges p={p} />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDay = () => {
    const events = (eventsByDate[keyOf(anchor)] || []).slice().sort((a, b) => (a.eventTime || '').localeCompare(b.eventTime || ''));
    if (events.length === 0) return <p style={{ textAlign: 'center', color: '#888', fontSize: '13px' }}>Нет событий на этот день.</p>;
    return events.map((p) => (
      <div key={p.id} style={{ ...urgencyStyle(urgencyLevel(p)), borderRadius: '10px', padding: '12px', marginBottom: '10px', border: '1px solid rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong>{p.eventTime || 'Без времени'}</strong>
          <span style={{ fontSize: '12px', opacity: 0.8 }}>
            {p.authorName || ''} · {sourceOf(p)}
          </span>
        </div>
        <p style={{ margin: '6px 0', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{p.text}</p>
        <ReactionBadges p={p} />
      </div>
    ));
  };

  return (
    <div style={{ background: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '12px', padding: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      {/* Всплывающие напоминания */}
      <div style={{ position: 'fixed', top: '70px', right: '15px', zIndex: 1200, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {toasts.map((t) => (
          <div key={t.id} style={{ background: '#8b0000', color: '#fff', padding: '10px 14px', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.3)', fontSize: '13px', maxWidth: '280px' }}>
            {t.msg}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
        <h3 style={{ margin: 0, color: '#1e3c72' }}>
          📅 Календарь{effectiveName ? `: ${effectiveName}` : ' событий'}
        </h3>

        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Выбор пользователя — только админ/модератор */}
          {isStaff && (
            <select
              value={calUserId || ''}
              onChange={(e) => setCalUserId(e.target.value || null)}
              title="Чей календарь показать"
              style={{ padding: '5px 8px', borderRadius: '12px', border: '1px solid #ddd', fontSize: '12px', background: '#fff', cursor: 'pointer', maxWidth: '160px' }}
            >
              <option value="">Мой календарь</option>
              {usersList
                .filter((u) => u.id !== user?.uid)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName || (u.email ? u.email.split('@')[0] : 'Пользователь')}
                  </option>
                ))}
            </select>
          )}

          <div style={{ display: 'flex', gap: '6px' }}>
            <button style={tabStyle(mode === 'month')} onClick={() => setMode('month')}>Месяц</button>
            <button style={tabStyle(mode === 'week')} onClick={() => setMode('week')}>Неделя</button>
            <button style={tabStyle(mode === 'day')} onClick={() => setMode('day')}>День</button>
          </div>

          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button onClick={() => shift(-1)} style={navBtn}>←</button>
            <span style={{ fontWeight: 700, color: '#1e3c72', minWidth: '150px', textAlign: 'center' }}>{label}</span>
            <button onClick={() => shift(1)} style={navBtn}>→</button>
            <button onClick={exportXls} title="Выгрузить события в XLS"
              style={{ ...navBtn, padding: '4px 12px', background: '#4caf50', color: '#fff' }}>
              ⬇️ XLS
            </button>
          </div>
        </div>
      </div>

      {mode === 'month' && renderMonth()}
      {mode === 'week' && renderWeek()}
      {mode === 'day' && renderDay()}

      <p style={{ fontSize: '11px', color: '#888', margin: '10px 0 0 0' }}>
        Месяц: цвет дня — по реакции пользователя (✅ зелёный, 🎯 синий,  оранжевый); при приближении времени — 🟡 за час, 🔴 за полчаса, багровый за 10 минут. Неделя/день: карточки с реакциями участников.
        {isStaff && ' Вы можете посмотреть и выгрузить календарь любого пользователя.'}
      </p>
    </div>
  );
};

export default EventCalendar;